import { CalendarToday, Monitor, Send, Sync } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { CALIBRATE_FONT_FAMILY_ACCENT } from "components/hoc/Theme";
import type {
  RelationshipTileConfig,
  RelationshipTileKey,
} from "types/partners";

const TILE_CONFIG: Record<RelationshipTileKey, RelationshipTileConfig> = {
  data_migration: {
    key: "data_migration",
    title: "Data Migration",
    description: "Move data between systems to keep payroll and HR aligned.",
    icon: "Send",
  },
  integration: {
    key: "integration",
    title: "Integration",
    description: "Connect systems to automate data flow across platforms.",
    icon: "Sync",
  },
  implementation: {
    key: "implementation",
    title: "Implementation & Consulting",
    description: "Support for setup, configuration, and ongoing system use.",
    icon: "CalendarToday",
  },
  hr_services: {
    key: "hr_services",
    title: "HR Services",
    description: "Support across core HR processes.",
    icon: "Monitor",
  },
};

const ICON_MAP = {
  Send: Send,
  Sync: Sync,
  CalendarToday: CalendarToday,
  Monitor: Monitor,
};

interface RelationshipTileProps {
  tileKey: RelationshipTileKey;
}

export default function RelationshipTile({ tileKey }: RelationshipTileProps) {
  const config = TILE_CONFIG[tileKey];
  const IconComponent = ICON_MAP[config.icon as keyof typeof ICON_MAP] || Send;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        p: 2,
        backgroundColor: "rgba(224, 242, 241, 0.5)", // #E0F2F1 50%
        borderRadius: "8px",
        alignItems: "flex-start",
      }}
    >
      {/* Icon Container */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "8px",
          backgroundColor: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <IconComponent
          sx={{
            fontSize: 20,
            color: "calibrate.iconOnTeal",
          }}
        />
      </Box>

      {/* Text Content */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "text.primary",
            mb: 0.5,
          }}
        >
          {config.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            fontSize: "0.75rem",
            color: "text.secondary",
            lineHeight: 1.4,
          }}
        >
          {config.description}
        </Typography>
      </Box>
    </Box>
  );
}
